import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { FormControlLabel, FormHelperText, Checkbox } from '@material-ui/core';
import { Form } from '@unform/web';
import { PesquisaTabela } from '~/components/Form/v2';
import { Spinner } from '~/components';
import {
  Input,
  Autocomplete,
  DateTime,
  Combobox,
  ComboboxMultiple,
  Select,
} from '~/components/Form';
import { isEmpty, isArray } from 'lodash';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { parse, format } from 'date-fns';
import { t, validCharacters } from '~/helpers';
import api from '~/services/api';
import { compareIsAfterDates } from '~/helpers/dateTime';
import { Container } from './styles';
import {
  MODULO_CUSTOMIZACAO_RUMO,
  MODULO_CUSTOMIZACAO_VLI,
} from '~/constants/modules';

export default function FimPitStop({
  data,
  reference,
  close,
  cancelarAcao,
  setIsValidating,
}) {
  const { client } = useSelector((state) => state.user.profile);
  const inputRef = React.useRef(null);
  const [initials, setInitials] = React.useState({});
  const [slapitstopposto, setSlapitstopposto] = React.useState(null);
  const [dataini, setDataini] = React.useState(null);
  const [datafim, setDatafim] = React.useState(null);
  const [confirmasla, setConfirmasla] = React.useState(false);
  const [confirmado, setConfirmado] = React.useState(false);
  const [regraVli, setRegraVli] = React.useState(null);
  const params = {};
  const modules = useSelector((state) => state.user.modules);
  const clienteRumo = modules.includes(MODULO_CUSTOMIZACAO_RUMO);
  const clienteVli = modules.includes(MODULO_CUSTOMIZACAO_VLI);

  const options = useMemo(
    () => [
      { value: 'S', label: t('s.geral.opcoes.sim') },
      { value: 'N', label: t('s.geral.opcoes.nao') },
    ],
    []
  );

  async function handleSubmit(param) {
    try {
      const schema = Yup.object().shape({
        codigoatendimento: Yup.number()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .typeError(t('s.geral.validacao.campoobrigatorio')),
        codigousuarioresponsavel: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .typeError(t('s.geral.validacao.campoobrigatorio')),
        dataencosteatendimento: Yup.mixed()
          .transform((value) =>
            value ? parse(value, 'dd/MM/yyyy HH:mm:ss', new Date()) : null
          )
          .required(t('s.geral.validacao.campoobrigatorio'))
          .test(
            'dataencosteatendimento',
            t('s.cadastro.pitstop.datahoraatendimentodevesersersuperiorturno'),
            () =>
              compareIsAfterDates({
                compare: param.datahorainicialturno,
                current: param.dataencosteatendimento,
              })
          ),
        dataliberacaoatendimento: Yup.mixed()
          .transform((value) =>
            value ? parse(value, 'dd/MM/yyyy HH:mm:ss', new Date()) : null
          )
          .required(t('s.geral.validacao.campoobrigatorio'))
          .test(
            'dataliberacaoatendimento',
            t('s.cadastro.pitstop.datahoraatendimentodevesersersuperiorturno'),
            () =>
              compareIsAfterDates({
                compare: param.datahorainicialturno,
                current: param.dataliberacaoatendimento,
              })
          )
          .test(
            'dataliberacaoatendimento',
            t('s.geral.validacao.campoobrigatorio'),
            function (value) {
              if (!value) return false;
              return true;
            }
          ),
        removeubicoatendimento: clienteRumo
          ? Yup.string()
              .nullable()
              .required(t('s.geral.validacao.campoobrigatorio'))
              .typeError(t('s.geral.validacao.campoobrigatorio'))
          : Yup.string().nullable(),
        removeubaldeatendimento: clienteRumo
          ? Yup.string()
              .nullable()
              .required(t('s.geral.validacao.campoobrigatorio'))
              .typeError(t('s.geral.validacao.campoobrigatorio'))
          : Yup.string().nullable(),
        removeuaterramentoatendimento: clienteRumo
          ? Yup.string()
              .nullable()
              .required(t('s.geral.validacao.campoobrigatorio'))
              .typeError(t('s.geral.validacao.campoobrigatorio'))
          : Yup.string().nullable(),
        nomeresponsavelclienteatendimento: clienteRumo
          ? Yup.string()
              .required(t('s.geral.validacao.campoobrigatorio'))
              .typeError(t('s.geral.validacao.campoobrigatorio'))
              .trim()
              .min(1, t('s.geral.validacao.campoobrigatorio'))
              .max(100, t('s.geral.validacao.tamanhomaximo').replace('%s', 100))
              .test(
                'characters-invalid',
                t('s.configuracao.geral.naopodecontercaracterespecial'),
                function (value) {
                  return validCharacters(value);
                }
              )
          : Yup.string().nullable(),
        observacaoatendimento: Yup.string().when(
          ['dataencosteatendimento', 'dataliberacaoatendimento'],
          {
            is: (dataencosteatendimento, dataliberacaoatendimento) => {
              if (!dataencosteatendimento || !dataliberacaoatendimento)
                return false;
              const differenceInMs =
                new Date(dataliberacaoatendimento) -
                new Date(dataencosteatendimento);
              const differenceInHours = differenceInMs / (1000 * 60);
              return differenceInHours > slapitstopposto;
            },
            then: Yup.string()
              .ensure()
              .trim()
              .required(t('s.geral.validacao.campoobrigatorio'))
              .max(500, t('s.geral.validacao.tamanhomaximo').replace('%s', 500))
              .test(
                'characters-invalid',
                t('s.configuracao.geral.naopodecontercaracterespecial'),
                function (value) {
                  return validCharacters(value);
                }
              ),
            otherwise: Yup.string()
              .ensure()
              .nullable()
              .trim()
              .max(500, t('s.geral.validacao.tamanhomaximo').replace('%s', 500))
              .test(
                'characters-invalid',
                t('s.configuracao.geral.naopodecontercaracterespecial'),
                function (value) {
                  return validCharacters(value);
                }
              ),
          }
        ),
        regras: Yup.array().when(
          ['dataencosteatendimento', 'dataliberacaoatendimento'],
          {
            is: (dataencosteatendimento, dataliberacaoatendimento) => {
              if (!dataencosteatendimento || !dataliberacaoatendimento)
                return false;
              const differenceInMs =
                new Date(dataliberacaoatendimento) -
                new Date(dataencosteatendimento);
              const differenceInHours = differenceInMs / (1000 * 60);
              return differenceInHours > slapitstopposto;
            },
            then: clienteVli
              ? Yup.array().nullable()
              : Yup.array()
                  .min(1, t('s.geral.validacao.campoobrigatorio'))
                  .required(t('s.geral.validacao.campoobrigatorio')),
            otherwise: Yup.array().ensure().nullable(),
          }
        ),
        regraVli: clienteVli
          ? Yup.number().when(
              ['dataencosteatendimento', 'dataliberacaoatendimento'],
              {
                is: (dataencosteatendimento, dataliberacaoatendimento) => {
                  if (!dataencosteatendimento || !dataliberacaoatendimento)
                    return false;
                  return (
                    (new Date(dataliberacaoatendimento) -
                      new Date(dataencosteatendimento)) /
                      (1000 * 60) >
                    slapitstopposto
                  );
                },
                then: Yup.number()
                  .required(t('s.geral.validacao.campoobrigatorio'))
                  .typeError(t('s.geral.validacao.campoobrigatorio')),
                otherwise: Yup.number().nullable(),
              }
            )
          : Yup.number().nullable(),
        idobservacaoatendimentoitem: clienteVli
          ? Yup.number().when(
              ['dataencosteatendimento', 'dataliberacaoatendimento'],
              {
                is: (dataencosteatendimento, dataliberacaoatendimento) => {
                  if (!dataencosteatendimento || !dataliberacaoatendimento)
                    return false;
                  return (
                    (new Date(dataliberacaoatendimento) -
                      new Date(dataencosteatendimento)) /
                      (1000 * 60) >
                    slapitstopposto
                  );
                },
                then: Yup.number()
                  .required(t('s.geral.validacao.campoobrigatorio'))
                  .typeError(t('s.geral.validacao.campoobrigatorio')),
                otherwise: Yup.number().nullable(),
              }
            )
          : Yup.number().nullable(),
      });
      await schema.validate(param, {
        abortEarly: false,
      });
      if (confirmasla && !confirmado) {
        toast.error(t('s.cadastro.pitstop.mensagemconfirmacaosuperacaosla'));
        setIsValidating(true);
        return;
      }
      const { regraVli: regraSelecionadaVli, ...paramSend } = param;
      const initialsSend = { ...initials };
      delete initialsSend.regraVli;
      const objSend = {
        ...initialsSend,
        ...paramSend,
        slapitstopposto: slapitstopposto,
        idcliente: client,
        regras: clienteVli
          ? regraSelecionadaVli
            ? [regraSelecionadaVli]
            : []
          : paramSend.regras || [],
      };
      await api.put('pitstop/fim', objSend);
      await close();
      toast.success(t('s.geral.sucessoalteracao'));
    } catch (err) {
      if (err && isArray(err)) {
        toast.error(err[0]);
      }
      setIsValidating(false);
      await cancelarAcao();
      const validationErrors = {};
      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });
        reference.current.setErrors(validationErrors);
      }
    }
  }

  const cellsVehicle = useMemo(
    () => [
      {
        id: 'idveiculo',
        label: t('s.configuracao.comboio.idveiculo'),
        filter: { type: 'text' },
      },
      {
        id: 'nomemodeloveiculo',
        label: t('s.menu.cadastro.veiculo.modelo'),
        filter: { type: 'text' },
      },
    ],
    []
  );

  const cellsUser = useMemo(
    () => [
      {
        id: 'codigousuario',
        label: t('s.cadastro.usuario.codigousuario'),
        filter: { type: 'text' },
      },
      {
        id: 'nomeusuario',
        label: t('s.cadastro.usuario.nomeusuario'),
        filter: { type: 'text' },
      },
    ],
    []
  );

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    async function handleCarregaAtendimento(idatendimento) {
      const { data } = await api.get(`/pitstop/${idatendimento}`);
      setInitials(data);
      const idRegraVli =
        data.regras && data.regras.length > 0 ? data.regras[0] : null;
      setRegraVli(idRegraVli);
      setDataini(data.dataencosteatendimento);
      setDatafim(data.dataliberacaoatendimento || Date());
      reference.current.setData({ ...data, regraVli: idRegraVli });
    }
    if (data != null) {
      handleCarregaAtendimento(data.idatendimento);
      setSlapitstopposto(data.slapitstopposto);
    }
  }, [data]);

  function verificaPeriodoInicial(value) {
    setDataini(value);
    verificaSla(value, datafim);
  }

  function verificaPeriodoFinal(value) {
    setDatafim(value);
    verificaSla(dataini, value);
  }

  function verificaSla(dtini, dtfim) {
    if (dtini && dtfim) {
      let dataInicial = parse(dtini, 'dd/MM/yyyy HH:mm:ss', new Date());
      let dataFinal = parse(dtfim, 'dd/MM/yyyy HH:mm:ss', new Date());
      const differenceInMs = new Date(dataFinal) - new Date(dataInicial);
      const differenceInHours = differenceInMs / (1000 * 60);
      setConfirmasla(differenceInHours > slapitstopposto);
    }
  }

  const handleCheckboxChange = (event) => {
    setConfirmado(event.target.checked);
  };

  return (
    <>
      {isEmpty(initials) ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : (
        <>
          <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
            <Container>
              <Autocomplete
                name="idposto"
                label="codigonomeposto"
                url="postoscomboios"
                unique="idposto"
                title={t('s.geral.postocomboio')}
                disabled
                variant="outlined"
              />
              <DateTime
                label={t('s.menu.operacao.turno')}
                name="datahorainicialturno"
                serialize={(value) =>
                  value.set({ second: 0 }).format('DD/MM/YYYY HH:mm:ss')
                }
                time
                disabled
              />
              <Input
                name="codigoatendimento"
                label={t('s.cadastro.pitstop.codigoatendimento')}
                disabled={true}
                style={{ marginTop: 16 }}
              />
              <div>
                <PesquisaTabela
                  name="idveiculo"
                  label={t('s.menu.cadastro.veiculo')}
                  primary="idveiculo"
                  secondary="nomemodeloveiculo"
                  url="veiculos/locomotivas"
                  header={cellsVehicle}
                  disabled={true}
                />
              </div>
              <DateTime
                label={t('s.cadastro.pitstop.dataencosteatendimento')}
                name="dataencosteatendimento"
                serialize={(value) =>
                  value.set({ second: 0 }).format('DD/MM/YYYY HH:mm:ss')
                }
                time
                disabled
                style={{ marginTop: 16 }}
                getValue={(value) => verificaPeriodoInicial(value)}
              />
              <DateTime
                label={t('s.cadastro.pitstop.dataliberacaoatendimento')}
                name="dataliberacaoatendimento"
                nullable
                serialize={(value) =>
                  value.set({ second: 0 }).format('DD/MM/YYYY HH:mm:ss')
                }
                time
                inputRef={inputRef}
                style={{ marginTop: 16 }}
                getValue={(value) => verificaPeriodoFinal(value)}
              />
              {clienteRumo && (
                <>
                  <Select
                    name="removeubicoatendimento"
                    variant="outlined"
                    label={t('s.cadastro.pitstop.removeubicoatendimento')}
                    options={[
                      { value: 'S', label: t('s.geral.opcoes.sim') },
                      { value: 'N', label: t('s.geral.opcoes.nao') },
                    ]}
                    style={{ marginTop: 16 }}
                  />
                  <Select
                    name="removeubaldeatendimento"
                    variant="outlined"
                    label={t('s.cadastro.pitstop.removeubaldeatendimento')}
                    options={[
                      { value: 'S', label: t('s.geral.opcoes.sim') },
                      { value: 'N', label: t('s.geral.opcoes.nao') },
                    ]}
                  />
                  <Select
                    name="removeuaterramentoatendimento"
                    variant="outlined"
                    label={t(
                      's.cadastro.pitstop.removeuaterramentoatendimento'
                    )}
                    options={[
                      { value: 'S', label: t('s.geral.opcoes.sim') },
                      { value: 'N', label: t('s.geral.opcoes.nao') },
                    ]}
                  />
                </>
              )}
              {confirmasla && (
                <>
                  <FormControlLabel
                    style={{ marginTop: 5 }}
                    control={
                      <Checkbox
                        name="confirmado"
                        checked={confirmado}
                        onChange={handleCheckboxChange}
                      />
                    }
                    label={t('s.cadastro.pitstop.mensagemsuperacaosla')}
                  />
                  {confirmasla && !confirmado && (
                    <FormHelperText error>
                      {t('s.cadastro.pitstop.mensagemconfirmacaosuperacaosla')}
                    </FormHelperText>
                  )}
                </>
              )}
              <div style={{ marginTop: 16 }}>
                <PesquisaTabela
                  name="codigousuarioresponsavel"
                  label={t('s.cadastro.pitstop.nomeusuarioresponsavel')}
                  primary="codigousuario"
                  secondary="nomeusuario"
                  url="usuarios"
                  header={cellsUser}
                  aguarda={true}
                />
              </div>
              {clienteRumo && (
                <Input
                  name="nomeresponsavelclienteatendimento"
                  label={t(
                    's.cadastro.pitstop.nomeresponsavelclienteatendimento'
                  )}
                  style={{ marginTop: 16 }}
                />
              )}
              <Input
                style={{ marginTop: clienteRumo ? 0 : 16 }}
                name="observacaoatendimento"
                label={t('s.cadastro.pitstop.observacaoatendimento')}
                multiline
                rows={3}
                inputProps={{ maxLength: 500 }}
              />
              {confirmasla && (
                <>
                  {clienteVli ? (
                    <>
                      <Combobox
                        name="regraVli"
                        label="descricaoobservacao"
                        url="pitstop/regra"
                        unique="idobservacaoatendimento"
                        title={t(
                          's.relatorio.alteracaomodooperacional.justificativa'
                        )}
                        params={{ pesquisalista: ['true'] }}
                        getValue={(value) => {
                          const id = value.idobservacaoatendimento || null;
                          if (id !== regraVli) {
                            setRegraVli(id);
                            reference.current.setFieldValue(
                              'idobservacaoatendimentoitem',
                              ''
                            );
                          }
                        }}
                        style={{ marginBottom: 10 }}
                      />
                      {regraVli && (
                        <Combobox
                          key={regraVli}
                          name="idobservacaoatendimentoitem"
                          label="descricaoobservacaoatendimentoitem"
                          url={`pitstop/regra/${regraVli}/itens`}
                          unique="idobservacaoatendimentoitem"
                          title="Item"
                          style={{ marginBottom: 10 }}
                        />
                      )}
                    </>
                  ) : (
                    <ComboboxMultiple
                      name="regras"
                      label="descricaoobservacao"
                      url="pitstop/regra"
                      unique="idobservacaoatendimento"
                      title={t(
                        's.relatorio.alteracaomodooperacional.justificativa'
                      )}
                      params={{ pesquisalista: ['true'] }}
                      style={{ marginBottom: 10 }}
                    />
                  )}
                </>
              )}
            </Container>
          </Form>
        </>
      )}
    </>
  );
}
